'use client';

/**
 * LibertyXUAT Landing Page
 * Showcases speed, AI, and institutional-grade trading power
 */

import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Zap,
  BrainCircuit,
  Shield,
  TrendingUp,
  Globe,
  BarChart3,
  Lock,
  Gauge,
} from 'lucide-react';

export default function RootPage() {
  const { user, signOut, loading } = useAuth();
  const router = useRouter();

  // Show loading animation while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background grid-pattern">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4 glow-cyan" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Authenticated users go to dashboard
  if (user) {
    router.push('/dashboard');
    return null;
  }

  // Landing page for unauthenticated users
  return (
    <div className="min-h-screen bg-background grid-pattern">
      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-10">
              <Image
                src="/images/mockups/projectlogo.png"
                alt="LibertyXUAT"
                fill
                className="object-contain"
              />
            </div>
            <span className="text-xl font-bold">
              <span className="text-primary">Liberty</span>
              <span className="text-accent">XUAT</span>
            </span>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" asChild>
              <Link href="/login">Sign In</Link>
            </Button>
            <Button className="border-glow-cyan" asChild>
              <Link href="/signup">Get Started</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto flex min-h-screen flex-col items-center justify-center px-4 pt-16">
        <div className="text-center">
          {/* Badge */}
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm">
            <Zap className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-primary font-medium">Sub-Millisecond Execution</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl font-bold tracking-tight sm:text-7xl lg:text-8xl">
            Trade at the
            <br />
            <span className="text-gradient-cyan-magenta">Speed of Thought</span>
          </h1>

          {/* Subheading */}
          <p className="mx-auto mt-6 max-w-2xl text-xl text-muted-foreground sm:text-2xl">
            AI-powered CFD & Forex platform with institutional-grade execution.
            <br />
            <span className="text-foreground font-semibold">Dominate volatile markets.</span>
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Button size="lg" className="border-glow-cyan h-14 px-8 text-lg" asChild>
              <Link href="/signup">
                <TrendingUp className="mr-2 h-5 w-5" />
                Start Trading
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-8 text-lg" asChild>
              <Link href="/dashboard">
                View Live Demo
              </Link>
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-success" />
              <span>Regulated & Secure</span>
            </div>
            <div className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-primary" />
              <span>0.847ms Avg Latency</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-accent" />
              <span>24/7 Global Markets</span>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mt-32 grid w-full max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="border-border bg-card/50 backdrop-blur transition-all hover:bg-card hover:border-primary/30">
            <CardHeader>
              <Zap className="h-12 w-12 text-primary glow-cyan" />
              <CardTitle className="mt-4 text-2xl">Lightning Fast</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Sub-millisecond order execution with direct market access.
                Every microsecond counts in volatile markets.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-border bg-card/50 backdrop-blur transition-all hover:bg-card hover:border-accent/30">
            <CardHeader>
              <BrainCircuit className="h-12 w-12 text-accent glow-magenta" />
              <CardTitle className="mt-4 text-2xl">AI-Powered Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Real-time trade signals with 87% average confidence.
                AI analyzes millions of data points every second.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-border bg-card/50 backdrop-blur transition-all hover:bg-card hover:border-success/30">
            <CardHeader>
              <Shield className="h-12 w-12 text-success glow-green" />
              <CardTitle className="mt-4 text-2xl">Risk Management</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Advanced portfolio risk scoring and automated stop-loss
                protection keep your capital safe.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-border bg-card/50 backdrop-blur transition-all hover:bg-card hover:border-primary/30">
            <CardHeader>
              <BarChart3 className="h-12 w-12 text-primary" />
              <CardTitle className="mt-4 text-2xl">Pro Charts</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                TradingView-grade charts with technical indicators and
                real-time data across all timeframes.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-border bg-card/50 backdrop-blur transition-all hover:bg-card hover:border-accent/30">
            <CardHeader>
              <Lock className="h-12 w-12 text-accent" />
              <CardTitle className="mt-4 text-2xl">Institutional Grade</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Bank-level security, multi-region redundancy, and 99.99%
                uptime SLA for professional traders.
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-border bg-card/50 backdrop-blur transition-all hover:bg-card hover:border-success/30">
            <CardHeader>
              <Globe className="h-12 w-12 text-success" />
              <CardTitle className="mt-4 text-2xl">Global Markets</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Trade Forex, CFDs, Crypto, Commodities, and Indices.
                Access every major market from one platform.
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Stats Section */}
        <div className="mt-32 w-full max-w-5xl">
          <div className="rounded-2xl border border-border bg-card/30 p-12 backdrop-blur">
            <div className="grid gap-12 sm:grid-cols-3">
              <div className="text-center">
                <p className="font-mono text-5xl font-bold text-primary">0.847ms</p>
                <p className="mt-2 text-sm text-muted-foreground">Average Execution</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-5xl font-bold text-success">$2.4B</p>
                <p className="mt-2 text-sm text-muted-foreground">Daily Volume</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-5xl font-bold text-accent">99.99%</p>
                <p className="mt-2 text-sm text-muted-foreground">Uptime SLA</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-32 mb-16 text-center">
          <h2 className="text-4xl font-bold">Ready to Trade Smarter?</h2>
          <p className="mt-4 text-xl text-muted-foreground">
            Join thousands of traders using LibertyXUAT
          </p>
          <Button size="lg" className="mt-8 border-glow-cyan h-14 px-8 text-lg" asChild>
            <Link href="/signup">
              Create Free Account
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
